import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { dequeue, listPending, markAttempted, type QueuedMutation } from './offlineQueue';
import { logWarn } from './logger';

/**
 * Schema-agnostic handle to the client. The offline queue replays mutations
 * against table names only known at runtime, so the generated per-table typing
 * cannot apply here — the untyped default client is the intended seam.
 */
const db = supabase as unknown as SupabaseClient;

/**
 * Rejoue la queue de mutations offline contre Supabase.
 *
 * - Appelé manuellement (au retour de navigator.onLine)
 * - Appelé périodiquement (interval / SW background sync)
 *
 * Stratégie d'erreur :
 *   - 4xx (sauf 409) : la mutation est invalide → on supprime de la queue + log
 *   - 409 (conflit) : on garde mais on stoppe le flush (intervention requise)
 *   - 5xx / network : on garde, attempt++ pour backoff
 *
 * Retourne le résumé pour l'UI.
 */

export interface FlushResult {
  attempted: number;
  succeeded: number;
  dropped: number; // mutations invalides supprimées
  pending: number; // restent en queue (retry plus tard)
}

export async function flushOfflineQueue(): Promise<FlushResult> {
  const items = await listPending();
  let succeeded = 0;
  let dropped = 0;

  for (const item of items) {
    const status = await replayOne(item);
    if (status === 'ok') {
      await dequeue(item.id);
      succeeded++;
    } else if (status === 'drop') {
      await dequeue(item.id);
      dropped++;
    } else {
      await markAttempted(item.id);
      // Si attempts > 5, on drop (mais on log)
      if (item.attempts + 1 >= 5) {
        await dequeue(item.id);
        dropped++;
        logWarn('offline', 'mutation dropped after 5 attempts', {
          mutationId: item.id,
          table: item.table,
          op: item.op,
        });
      }
    }
  }

  return {
    attempted: items.length,
    succeeded,
    dropped,
    pending: items.length - succeeded - dropped,
  };
}

type ReplayStatus = 'ok' | 'retry' | 'drop';

async function replayOne(m: QueuedMutation): Promise<ReplayStatus> {
  try {
    if (m.op === 'insert') {
      const { error } = await db.from(m.table).upsert(
        { ...m.payload, client_request_id: m.id },
        { onConflict: 'client_request_id', ignoreDuplicates: false },
      );
      return classifyError(error);
    }
    if (m.op === 'update') {
      if (!m.match) return 'drop';
      let q = db.from(m.table).update(m.payload);
      for (const [k, v] of Object.entries(m.match)) {
        q = q.eq(k, v as string | number | boolean);
      }
      const { error } = await q;
      return classifyError(error);
    }
    if (m.op === 'delete') {
      if (!m.match) return 'drop';
      let q = db.from(m.table).delete();
      for (const [k, v] of Object.entries(m.match)) {
        q = q.eq(k, v as string | number | boolean);
      }
      const { error } = await q;
      return classifyError(error);
    }
    return 'drop';
  } catch {
    return 'retry';
  }
}

/**
 * Classifie une erreur Supabase pour décider du sort de la mutation.
 * Exporté pour les tests unitaires.
 */
export function classifyError(error: { code?: string; message?: string } | null): ReplayStatus {
  if (!error) return 'ok';
  const code = error.code ?? '';
  if (code === '23505') return 'ok'; // unique violation — déjà appliquée (idempotent)
  if (code.startsWith('23')) return 'drop'; // contrainte (FK, check) — invalide, on abandonne
  return 'retry';
}
